import type { MonthlyReviewSummaryRow } from '@repo/shared-types';
import type { chotNudgeState } from './chotNudge';
import type { TermNudgeState } from './termNudge';

/** One row of the workbench "Cần chú ý" card. Pure data: the component decides
 *  how to render, the P0.4 tab-bar dot only needs `length > 0`. */
export type AttentionItem =
  | { kind: 'chot'; key: 'chot'; variant: 'owed' | 'pending' | 'ready'; month: string; prev: string; owed: string | null; pending: number; to: '/invoices/close-month' }
  | { kind: 'termOut'; key: string; studentId: string; studentName: string; to: string }
  | { kind: 'termNearlyOut'; key: string; studentId: string; studentName: string; remaining: number; to: string }
  | { kind: 'termMore'; key: 'termMore'; count: number; to: '/students' }
  | { kind: 'unmatched'; key: 'unmatched'; count: number; total: number; to: '/payments/unmatched' }
  | { kind: 'reconcileExhausted'; key: 'reconcileExhausted'; to: '/settings/billing' }
  /** THI-105/THI-189: "Nhận xét tháng {{month}} · {{subject}} · {{done}}/{{total}}
   *  đã viết" for one subject whose monthly-review sweep isn't finished yet.
   *  Unlike every other row here, NOT gated on manage:invoices — writing
   *  reviews is a MEMBER's daily job too (see useAttentionItems). */
  | { kind: 'monthlyReview'; key: string; subject: string; month: string; done: number; total: number; to: string }
  /** THI-189/THI-193 CD review item 5: TERM_ROWS_MAX (3) or more pending
   *  subjects collapse into this one row, pointing at the index page instead
   *  of a third (or fourth…) row — the Cần chú ý card already carries chốt
   *  sổ, gói hết buổi and giao dịch chưa khớp, so the review threshold is
   *  tighter than the per-student term rows. `done`/`total` are the ORG-WIDE
   *  sums over every subject in the summary (not just the pending ones —
   *  same figure `monthlyReviewsIndex.totalLine` shows), so the one row
   *  still carries the "x/N đã viết" the three separate rows used to. */
  | { kind: 'monthlyReviewMore'; key: 'monthlyReviewMore'; month: string; count: number; done: number; total: number; to: '/monthly-reviews' };

/** Handoff 02: one row per student, beyond this many collapse into "và N học viên khác". */
export const TERM_ROWS_MAX = 3;

export function monthFmt(ym: string): number {
  return Number(ym.slice(5, 7));
}

/** `oldestOwed` is unbounded backwards, so it can sit in an earlier year than
 *  the current month; "Tháng 12" in February would read as the December ahead.
 *  Format MM/YYYY whenever the years differ, bare month number otherwise. */
export function owedMonthFmt(ym: string, currentYm: string): number | string {
  return ym.slice(0, 4) === currentYm.slice(0, 4) ? Number(ym.slice(5, 7)) : `${ym.slice(5, 7)}/${ym.slice(0, 4)}`;
}

export function buildAttentionItems(input: {
  chot: ReturnType<typeof chotNudgeState>;
  month: string;
  prev: string;
  term: TermNudgeState;
  unmatched: { count: number; total: number };
  /** Month-to-date auto-reconcile quota is spent, so the org's BankHub company
   *  is Suspended. Last in the list: it is a state to know about, not a task
   *  with a deadline, and it must not push a chốt-sổ nudge down the card. */
  reconcile: { exhausted: boolean };
}): AttentionItem[] {
  const items: AttentionItem[] = [];
  const { chot, term, unmatched, month, prev } = input;

  if (chot.show) {
    const variant = chot.oldestOwed ? 'owed' : chot.pending > 0 ? 'pending' : 'ready';
    items.push({ kind: 'chot', key: 'chot', variant, month, prev, owed: chot.oldestOwed, pending: chot.pending, to: '/invoices/close-month' });
  }

  const termRows: AttentionItem[] = [
    ...term.out.map((r) => ({ kind: 'termOut' as const, key: r.enrollmentId, studentId: r.studentId, studentName: r.studentName, to: `/students/${r.studentId}` })),
    ...term.nearlyOut.map((r) => ({ kind: 'termNearlyOut' as const, key: r.enrollmentId, studentId: r.studentId, studentName: r.studentName, remaining: r.termState?.remaining ?? 0, to: `/students/${r.studentId}` })),
  ];
  if (termRows.length > TERM_ROWS_MAX) {
    items.push(...termRows.slice(0, TERM_ROWS_MAX));
    items.push({ kind: 'termMore', key: 'termMore', count: termRows.length - TERM_ROWS_MAX, to: '/students' });
  } else {
    items.push(...termRows);
  }

  if (unmatched.count > 0) {
    items.push({ kind: 'unmatched', key: 'unmatched', count: unmatched.count, total: unmatched.total, to: '/payments/unmatched' });
  }

  if (input.reconcile.exhausted) {
    items.push({ kind: 'reconcileExhausted', key: 'reconcileExhausted', to: '/settings/billing' });
  }
  return items;
}

/** THI-189, spec §10.3: one row per PENDING subject (written < total) for the
 *  month being reviewed. THI-193 CD review item 5: three or more pending
 *  subjects (`pending.length >= TERM_ROWS_MAX`, was `>`) collapse into one
 *  "còn {{k}} môn, {{done}}/{{total}} đã viết" row pointing at the index
 *  page — a workbench card row is pricier real estate than the index page's
 *  own rows, so the threshold is tighter than `termRows`' `TERM_ROWS_MAX`
 *  collapse above (which keeps its own `>` at 3). Source is the summary
 *  endpoint (one call, no per-slot fan-out — replaces the cycle-1
 *  `useSessions`-based single-slot guess entirely). */
export function monthlyReviewItems(summary: MonthlyReviewSummaryRow[], month: string): AttentionItem[] {
  const pending = summary.filter((r) => r.written < r.total);
  if (pending.length === 0) return [];
  if (pending.length >= TERM_ROWS_MAX) {
    // Org-wide, not just the pending subjects: matches the index page's own
    // totalLine figure, so the collapsed row and the page it links to never
    // disagree on "x/N đã viết".
    const total = summary.reduce((sum, r) => sum + r.total, 0);
    const done = summary.reduce((sum, r) => sum + r.written, 0);
    return [{
      kind: 'monthlyReviewMore', key: 'monthlyReviewMore', month, count: pending.length, done, total, to: '/monthly-reviews',
    }];
  }
  return pending.map((r) => ({
    kind: 'monthlyReview' as const,
    key: `monthlyReview-${r.subjectId}`,
    subject: r.subjectName,
    month,
    done: r.written,
    total: r.total,
    to: `/subjects/${r.subjectId}/monthly-reviews?month=${month}`,
  }));
}
